import type { User } from 'firebase/auth';

/**
 * Signing in on a developer's own machine, without Google.
 *
 * Firebase sign-in needs a Google popup that nothing automated can complete,
 * which leaves the whole signed-in half of the product unreachable while
 * working on it. Setting REACT_APP_DEV_AUTH_UID in frontend/.env.local puts a
 * fixed account in place of the popup, and the API accepts the matching token
 * only when its own DEV_AUTH_UID names the same account.
 *
 * Both halves are inert without their variable, and the `NODE_ENV` check here
 * means a production bundle never carries this at all.
 */
export const DEV_AUTH_TOKEN_PREFIX = 'dev-auth:';

export const devAuthUid = (): string | null => {
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }
  return (process.env.REACT_APP_DEV_AUTH_UID || '').trim() || null;
};

export const devAuthToken = (): string | null => {
  const uid = devAuthUid();
  return uid ? `${DEV_AUTH_TOKEN_PREFIX}${uid}` : null;
};

/**
 * Enough of a Firebase user for the app to treat the session as signed in.
 *
 * Only the fields the app actually reads are filled; anything reaching for the
 * rest of the Firebase surface is doing something this bypass cannot stand in
 * for, and should fail loudly rather than quietly get a stub.
 */
export const devAuthUser = (): User | null => {
  const uid = devAuthUid();
  if (!uid) {
    return null;
  }

  const token = `${DEV_AUTH_TOKEN_PREFIX}${uid}`;
  const email = (process.env.REACT_APP_DEV_AUTH_EMAIL || '').trim() || 'dev@localhost';

  return {
    uid,
    email,
    displayName: (process.env.REACT_APP_DEV_AUTH_NAME || '').trim() || 'Dev',
    photoURL: null,
    emailVerified: true,
    isAnonymous: false,
    phoneNumber: null,
    providerId: 'dev-auth',
    tenantId: null,
    metadata: {},
    providerData: [],
    refreshToken: token,
    getIdToken: async () => token,
    getIdTokenResult: async () => ({ token, claims: {}, authTime: '', issuedAtTime: '', expirationTime: '', signInProvider: 'dev-auth', signInSecondFactor: null }),
    reload: async () => {},
    delete: async () => {},
    toJSON: () => ({ uid, email }),
  } as unknown as User;
};
